import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem } from '@/lib/types/cart.types.ts';

interface InitialState {
	items: Array<CartItem>;
	isCartOpen: boolean;
}

interface Store extends InitialState {
	addItem: (item: CartItem, silent?: boolean) => void;
	updateItem: (item: Partial<CartItem>) => void;
	deleteItem: (id: number) => void;

	openCart: () => void;
	closeCart: () => void;
	toggleCartState: () => void;

	resetCart: () => void;
}

const initialState: InitialState = {
	items: [],
	isCartOpen: false,
};

const cartStore = create<Store>()(
	persist(
		(set, get) => {
			const addItem = (item: CartItem, silent?: boolean) => {
				const { openCart } = get();
				set((prev) => ({ ...prev, items: [...(prev?.items || []), item] }));

				if (silent) return;

				openCart();
			};

			const updateItem = (item: Partial<CartItem>) => {
				const { items } = get();
				const updatedItems = items?.map((i) => (i?.id === item?.id ? { ...i, ...item } : i));

				set({ items: updatedItems });
			};

			const deleteItem = (id: number) => {
				set((prev) => ({ ...prev, items: prev.items.filter((item) => item.id !== id) }));
			};

			const openCart = () => {
				set({ isCartOpen: true });
			};

			const closeCart = () => {
				set({ isCartOpen: false });
			};

			const toggleCartState = () => {
				const { isCartOpen } = get();
				set({ isCartOpen: !isCartOpen });
			};

			const resetCart = () => {
				set({ items: [] });
			};

			return {
				...initialState,
				addItem,
				updateItem,
				deleteItem,

				openCart,
				closeCart,
				toggleCartState,

				resetCart,
			};
		},
		{
			name: 'cart',
			partialize: (state) => ({ items: state.items }),
		},
	),
);

export default cartStore;
